import { useEffect, useState } from 'react'
import { CellProps } from 'react-table'
import { Link, RouteComponentProps } from 'react-router-dom'
import BaseTable from '../../components/BaseTable/BaseTable'
import DashboardWrapper from '../../components/MainDashboardWrapper/DashboardWrapper'
import { formatDate, formatDateTime } from '../../helpers/dateUtils'
import { campaignSignupUrl, conversionRate, getSignupCampaign } from './helper'

const formatMoney = (amount: number, currency: string) => {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount)
  } catch {
    return `${amount.toFixed(2)} ${currency}`
  }
}

const Tile = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="bg-white rounded-xl shadow-md p-6 flex flex-col gap-3">
    <span className="text-sm font-semibold text-gray-500">{label}</span>
    <span className="text-3xl font-extrabold text-[#0B1D3A] leading-none">{value}</span>
  </div>
)

export default function TrackingCampaignPage({ match }: RouteComponentProps<{ campaign: string }>) {
  const { campaign } = match.params
  const [detail, setDetail] = useState<any>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    setLoading(true)
    setError(false)
    getSignupCampaign(campaign)
      .then((res) => setDetail(res.data.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [campaign])

  const summary = detail?.summary
  const outcomes = detail?.outcomes
  const show = (value: any) => (loading ? '—' : value ?? 0)
  const revenue = Object.entries(outcomes?.revenue || {})
    .map(([currency, amount]) => formatMoney(amount as number, currency))
    .join(' + ')

  const signupColumns: any[] = [
    {
      Header: 'Name',
      accessor: 'displayName',
      Cell: (cellProps: CellProps<any>) => (
        <Link className="no-underline" to={`/all-users/${cellProps.row.original.userId}`}>
          {cellProps.value}
        </Link>
      ),
    },
    { Header: 'Email', accessor: 'email' },
    {
      Header: 'Type',
      accessor: 'userType',
      Cell: (cellProps: CellProps<any>) => (cellProps.value === 'coach' ? 'Coach' : 'Participant'),
    },
    {
      Header: 'Signed up',
      accessor: 'createdAt',
      Cell: (cellProps: CellProps<any>) => formatDateTime(cellProps.value),
    },
    {
      Header: 'Subscription',
      id: 'subscription',
      accessor: (row: any) => (row.subscription?.active ? row.subscription.plan || 'Subscribed' : ''),
      Cell: (cellProps: CellProps<any>) => {
        const { subscription, lookupFailed } = cellProps.row.original
        if (lookupFailed) return <span className="text-gray-400">Couldn't load</span>
        if (!subscription?.active) return <span className="text-gray-400">No</span>
        return (
          <span className="text-green-700 font-semibold">
            {subscription.plan || 'Subscribed'}
            {subscription.isLifetime ? ' (lifetime)' : ''}
          </span>
        )
      },
    },
    {
      Header: 'Listing purchases',
      id: 'purchases',
      accessor: (row: any) => row.purchases?.length || 0,
      Cell: (cellProps: CellProps<any>) => {
        const { purchases = [], lookupFailed } = cellProps.row.original
        if (lookupFailed) return <span className="text-gray-400">Couldn't load</span>
        if (purchases.length === 0) return <span className="text-gray-400">No</span>
        return (
          <div className="flex flex-col gap-1">
            {purchases.map((p: any) => (
              <span key={p.transactionId} className="text-green-700">
                {p.listingTitle || 'Listing'}
                {p.amount != null ? ` · ${formatMoney(p.amount, p.currency)}` : ''}
              </span>
            ))}
          </div>
        )
      },
    },
  ]

  const dailyColumns: any[] = [
    { Header: 'Day', accessor: 'day', Cell: (cellProps: CellProps<any>) => formatDate(`${cellProps.value}T12:00:00Z`) },
    { Header: 'Clicks', accessor: 'visits' },
    { Header: 'Accounts created', accessor: 'signups' },
    {
      Header: 'Conversion',
      id: 'conversion',
      accessor: (row: any) => conversionRate(row.signups, row.visits),
    },
  ]

  return (
    <DashboardWrapper page="tracking" title={`Signup Tracking — ${campaign}`}>
      <div className="flex flex-col gap-5">
        <div className="flex items-center justify-between">
          <Link to="/tracking" className="no-underline text-sm">
            ← All campaigns
          </Link>
          <span className="text-sm text-gray-500 font-mono break-all">{campaignSignupUrl(campaign)}</span>
        </div>

        {error && (
          <p className="text-sm text-red-600">Couldn't load tracking data from the server — reload to retry.</p>
        )}

        <div className="grid grid-cols-4 gap-4">
          <Tile label="Clicks" value={show(summary?.visits)} />
          <Tile label="Unique visitors" value={show(summary?.uniqueVisitors)} />
          <Tile label="Accounts created" value={show(summary?.signups)} />
          <Tile
            label="Conversion"
            value={loading ? '—' : conversionRate(summary?.signups || 0, summary?.visits || 0)}
          />
        </div>

        <div className="grid grid-cols-4 gap-4">
          <Tile label="Bought a subscription" value={show(outcomes?.subscribed)} />
          <Tile label="Bought a listing" value={show(outcomes?.purchasers)} />
          <Tile label="Listing purchases" value={show(outcomes?.purchases)} />
          <Tile label="Listing revenue" value={loading ? '—' : revenue || formatMoney(0, 'USD')} />
        </div>
        {!loading && outcomes?.lookupFailed > 0 && (
          <p className="text-sm text-red-600">
            Couldn't load subscription/purchase data for {outcomes.lookupFailed} account(s) — reload to retry.
          </p>
        )}

        <div className="bg-white rounded-xl shadow-md p-6">
          <p className="text-base font-bold mb-1">Accounts created</p>
          <p className="text-sm text-gray-500 mb-4">
            {show(summary?.coachSignups)} coaches · {show(summary?.participantSignups)} participants
          </p>
          <BaseTable data={detail?.signups || []} columns={signupColumns} loading={loading} />
        </div>

        <div className="bg-white rounded-xl shadow-md p-6">
          <p className="text-base font-bold mb-4">By day</p>
          <BaseTable data={detail?.daily || []} columns={dailyColumns} loading={loading} />
        </div>
      </div>
    </DashboardWrapper>
  )
}
